"use client"

import { useLocation } from "react-router-dom"

import { AdminRouteTabs } from "@/shared/components/admin-route-tabs"
import { MobileChipLinks } from "@/shared/components/mobile-list-chrome"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { useActiveBranchSlug, useActiveWorkspaceSlug } from "@/features/auth"
import { useFinancePageState } from "@/features/finance/hooks/use-finance-page-state"
import { FinanceSettingsTab } from "@/features/finance/model/finance-tabs"
import { buildBranchAdminUrl } from "@/shared/lib/branch/branch-admin-routing"
import { useTranslations } from "@/i18n"

const SETTINGS_TABS: Array<{ value: FinanceSettingsTab; labelKey: string }> = [
  { value: FinanceSettingsTab.Setup, labelKey: "setup" },
  // Accounts + Categories moved to the dedicated top-level "Comptes" (Chart of Accounts) page.
  { value: FinanceSettingsTab.Currency, labelKey: "currency" },
  { value: FinanceSettingsTab.FiscalPeriods, labelKey: "fiscalPeriods" },
  { value: FinanceSettingsTab.OpeningBalances, labelKey: "openingBalances" },
  { value: FinanceSettingsTab.Receipts, labelKey: "receipts" },
]

/** Route-driven Settings sub-tabs (each a real child route under /finance/settings). */
export function FinanceSettingsNav() {
  const t = useTranslations("finance.settings.subnav")
  const { activeContext, parishBranch } = useFinancePageState()
  const branchSlug = useActiveBranchSlug()
  const workspaceSlug = useActiveWorkspaceSlug()
  const { pathname, search } = useLocation()
  const scopedBranchSlug = activeContext === "parish" ? parishBranch?.slug ?? branchSlug ?? "" : branchSlug ?? ""
  const active =
    SETTINGS_TABS.find((tab) => pathname.includes(`/finance/settings/${tab.value}`))?.value ??
    FinanceSettingsTab.Setup

  const isMobile = useIsMobile()
  const items = SETTINGS_TABS.map((tab) => ({
    value: tab.value,
    label: t(tab.labelKey),
    href: `${buildBranchAdminUrl(scopedBranchSlug, `/finance/settings/${tab.value}`, workspaceSlug ?? undefined)}${search}`,
  }))

  // Phone: five sections do not fit a segmented control, so they are a sideways row of chips.
  if (isMobile) return <MobileChipLinks value={active} items={items} />
  return <AdminRouteTabs value={active} items={items} />
}
